import { toRenderableElements } from "./canvasRenderableElements";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Divider,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useCanvas,
  useCanvasData,
  useCanvasSubmit,
} from "../hooks/useCanvases";
import {
  useViewQuery,
  useWorkspaceObjectChangeQueryInvalidation,
} from "../hooks/useViewDataQuery";
import { emitWorkspaceObjectChange } from "../services/workspaceObjectChanges";
import {
  CanvasRenderer,
  CanvasViewGridRenderer,
  CanvasTimelineRenderer,
  CanvasDataWidgetRenderer,
  EmbeddedCanvasRenderer,
} from "../components/canvasBuilder";
import { CanvasRuntimeProvider } from "../components/canvasBuilder/CanvasRuntimeContext";
import {
  isInActiveScope,
  isWithinContainer,
  omitKeys,
  type ActivePanels,
  type ScopeNode,
} from "./canvasSelectionScope";
import type { CanvasInteractionEvent } from "../components/canvasBuilder/CanvasRuntimeContext";
import { CanvasWidgetDataContext } from "../components/canvasBuilder/elements/CanvasAggregateWidget";
import { CanvasThemeScope } from "../theme/CanvasThemeScope";
import { FloatingChatWidget } from "../components/MyVaultAgent";
import { canvasContentMaxWidth } from "../theme/canvasLayoutWidth";
import type {
  RenderableElement,
  FieldState,
} from "../components/canvasBuilder";
import type { CanvasDataSource, CanvasElement } from "../types";
import {
  isReadOnlyFieldConfig,
  isWritableAccess,
} from "./runtimeElementAccess";
import {
  cardFieldKey,
  cardValuesByPlainPath,
  cardValuesFromRecord,
  namespaceCardFields,
  nonCardValues,
  plainFieldPath,
} from "./canvasCardFieldKeys";

/** Default grid config for runtime rendering (used when canvas has no stored settings) */
const DEFAULT_RUNTIME_GRID_CONFIG = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 3,
};
const MIXED_FORM_SOURCE_MESSAGE =
  "This form has fields bound to multiple data sources. Bind all submitted fields to one writable view.";

/**
 * Form state type - maps field paths to values
 */
interface FormState {
  values: Record<string, unknown>;
  errors: Record<string, string>;
  touched: Set<string>;
}

/**
 * CanvasApp - Runtime execution of canvas applications
 * Renders the canvas form using grid-based layout matching the builder.
 */
export default function CanvasApp() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Canvas ID can be either a number or UUID string - use as-is
  const canvasId = id || null;
  const urlRowId = searchParams.get("row") || undefined;

  const { workspace, isEditor } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  useWorkspaceObjectChangeQueryInvalidation(workspaceId);

  // Data fetching
  const {
    canvas,
    loading: canvasLoading,
    error: canvasError,
    errorStatus: canvasErrorStatus,
  } = useCanvas(workspaceId, canvasId);
  const selectionContainer = useMemo(() => {
    const elements = (canvas?.elements || []) as Array<{
      id?: string;
      element_type?: string;
      config_json?: {
        selection_binding?: {
          source_grid_element_id: string;
          data_source_alias: string;
          // Stored JSON may omit mode; the API defaults it to "read".
          mode?: "read" | "edit";
          empty_state_text?: string;
        };
      };
    }>;
    return elements.find(
      (element) =>
        element.element_type?.toLowerCase() === "container" &&
        element.config_json?.selection_binding,
    );
  }, [canvas]);
  const selectionBinding = selectionContainer?.config_json?.selection_binding;
  const selectionContainerId = selectionContainer?.id
    ? String(selectionContainer.id)
    : null;
  // The selection card only governs the tab that holds it. Track which panel of
  // each tab control is shown so the other tabs keep their ordinary form.
  const [activePanels, setActivePanels] = useState<ActivePanels>({});
  const handleActivePanelChange = useCallback(
    (containerId: string, panelId: string) =>
      setActivePanels((prev) =>
        prev[containerId] === panelId
          ? prev
          : { ...prev, [containerId]: panelId },
      ),
    [],
  );
  const activeBinding =
    selectionBinding &&
    (!selectionContainerId ||
      isInActiveScope(
        selectionContainerId,
        (canvas?.elements || []) as ScopeNode[],
        activePanels,
      ))
      ? selectionBinding
      : undefined;
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  // Field paths of the fields inside the selection card. The selected record fills
  // only these, so the form on another tab never shows the selected row's values.
  const cardFieldPaths = useMemo(() => {
    if (!selectionContainerId) return null;
    const elements = (canvas?.elements || []) as Array<
      ScopeNode & {
        element_type?: string;
        data_binding?: { field_path?: string };
      }
    >;
    const paths = new Set<string>();
    for (const element of elements) {
      const path = element.data_binding?.field_path;
      if (
        element.id &&
        path &&
        ["field", "static"].includes(
          element.element_type?.toLowerCase() ?? "",
        ) &&
        isWithinContainer(String(element.id), selectionContainerId, elements)
      ) {
        paths.add(cardFieldKey(path));
      }
    }
    return paths;
  }, [canvas, selectionContainerId]);
  // The selection card governs only its own tab: there the row is the selected one.
  // A tab beside it whose fields are all read-only shows the canvas's primary record,
  // as on a canvas without a card. A tab with a writable field stays an empty create
  // form, so a submit there can never start from (or overwrite) a record it did not
  // create.
  const offCardTabReadOnly = useMemo(() => {
    if (!selectionContainerId) return false;
    const elements = (canvas?.elements || []) as Array<
      ScopeNode & {
        element_type?: string;
        effective_access?: string;
        config_json?: Record<string, unknown>;
      }
    >;
    const fields = elements.filter(
      (element) =>
        element.element_type?.toLowerCase() === "field" &&
        element.id &&
        isInActiveScope(String(element.id), elements, activePanels) &&
        !isWithinContainer(String(element.id), selectionContainerId, elements),
    );
    return (
      fields.length > 0 &&
      fields.every(
        (element) =>
          !isWritableAccess(element.effective_access as never) ||
          isReadOnlyFieldConfig(element.config_json),
      )
    );
  }, [activePanels, canvas, selectionContainerId]);
  const rowId = activeBinding
    ? selectedRowId || undefined
    : selectionBinding && !offCardTabReadOnly
      ? undefined
      : urlRowId;
  const isEditMode = !!rowId;
  const formSourceAliases = useMemo(() => {
    // A create form may use a writable source alongside read-only display sources.
    // Resolve its FIELD bindings before loading capabilities so can_create is read
    // from the form source rather than a read-only primary source.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sources = ((canvas as any)?.data_sources || []) as any[];
    const primary = sources.find((source) => source.is_primary) || sources[0];
    const primaryAlias = primary?.alias || primary?.id;
    const elements = (canvas?.elements || []) as Array<
      ScopeNode & {
        element_type?: string;
        data_binding?: {
          data_source_alias?: string;
          data_source_id?: string;
        };
      }
    >;
    const fields = elements.filter(
      (element) =>
        element.element_type?.toLowerCase() === "field" &&
        element.id &&
        isInActiveScope(String(element.id), elements, activePanels) &&
        !(
          selectionBinding &&
          selectionContainerId &&
          isWithinContainer(String(element.id), selectionContainerId, elements)
        ),
    );
    const aliases = new Set<string>();
    for (const field of fields) {
      const bound =
        field.data_binding?.data_source_alias ||
        field.data_binding?.data_source_id;
      const source = bound
        ? sources.find(
            (candidate) =>
              String(candidate.alias || candidate.id || "") === String(bound) ||
              String(candidate.name || "") === String(bound),
          )
        : primary;
      const alias = source?.alias || source?.name || source?.id || bound;
      if (alias) aliases.add(String(alias));
    }
    if (aliases.size === 0 && primaryAlias) aliases.add(String(primaryAlias));
    return Array.from(aliases);
  }, [activePanels, canvas, selectionBinding, selectionContainerId]);
  const formDataSourceAlias =
    formSourceAliases.length === 1 ? formSourceAliases[0] : undefined;
  const hasMixedFormSources = !activeBinding && formSourceAliases.length > 1;
  // Preserve the original primary-source query for existing canvases. The alias
  // override is needed only when the form source differs from the primary source.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawDataSourcesForForm = ((canvas as any)?.data_sources || []) as any[];
  const primaryFormSource =
    rawDataSourcesForForm.find((source) => source.is_primary) ||
    rawDataSourcesForForm[0];
  const primaryFormSourceAlias =
    primaryFormSource?.alias || primaryFormSource?.id;
  const formQuerySourceAlias =
    formDataSourceAlias && formDataSourceAlias !== primaryFormSourceAlias
      ? formDataSourceAlias
      : undefined;
  const formMetadataSourceAlias =
    activeBinding?.data_source_alias ||
    formDataSourceAlias ||
    primaryFormSourceAlias;
  const formMetadataSource = rawDataSourcesForForm.find(
    (source) =>
      String(source.alias || source.name || source.id || "") ===
      String(formMetadataSourceAlias || ""),
  );
  const formViewId = formMetadataSource?.view_id
    ? String(formMetadataSource.view_id)
    : null;
  const { data: formView, refetch: refetchFormView } = useViewQuery(formViewId);
  const {
    data: canvasData,
    primaryData,
    loadedFor,
    loading: dataLoading,
    error: dataError,
    refresh: refreshCanvasData,
  } = useCanvasData(
    workspaceId,
    canvasId,
    rowId,
    activeBinding?.data_source_alias ||
      (selectionBinding ? undefined : formQuerySourceAlias),
  );
  // The data hook keeps the previous query's data until a refetch lands. A tab other
  // than the card's must never show the card's record in that gap, so it only takes
  // data fetched for its own query (the primary record: no row id from a selection,
  // no card alias).
  const primaryDataIsCurrent =
    !loadedFor ||
    (loadedFor.rowId === (rowId ? String(rowId) : null) &&
      loadedFor.alias ===
        (activeBinding?.data_source_alias ||
          (selectionBinding ? null : formQuerySourceAlias) ||
          null));
  const primaryIdentityScope = JSON.stringify([
    workspaceId,
    canvasId,
    urlRowId ?? null,
  ]);
  const settledPrimaryIdentity = useRef<{
    scope: string;
    rowId: string | null;
  } | null>(null);
  if (settledPrimaryIdentity.current?.scope !== primaryIdentityScope) {
    settledPrimaryIdentity.current = {
      scope: primaryIdentityScope,
      rowId: null,
    };
  }
  if (!activeBinding && primaryDataIsCurrent) {
    const primaryResponse = primaryData ?? canvasData;
    if (primaryResponse) {
      settledPrimaryIdentity.current.rowId = primaryResponse.record_id ?? null;
    }
  }
  const selectedRecord = activeBinding
    ? selectedRowId && canvasData?.record_id === selectedRowId
      ? canvasData.record
      : null
    : selectionBinding && !offCardTabReadOnly
      ? null
      : primaryDataIsCurrent
        ? canvasData?.record
        : primaryData?.record;
  const {
    submit,
    loading: submitting,
    error: submitError,
    result,
  } = useCanvasSubmit(workspaceId);

  // Form state
  const [formState, setFormState] = useState<FormState>({
    values: {},
    errors: {},
    touched: new Set(),
  });
  const [submitted, setSubmitted] = useState(false);
  const [inlineSubmitSuccess, setInlineSubmitSuccess] = useState(false);
  const [formSourceError, setFormSourceError] = useState<string | null>(null);
  const initializedSelectionRef = useRef<string | null>(null);
  const initializedPrimaryRef = useRef<string | null>(null);
  const initializedPrimaryDataRef = useRef<typeof primaryData>(null);
  const selectedRowReady =
    !!selectedRowId &&
    !dataLoading &&
    !!selectedRecord &&
    initializedSelectionRef.current === selectedRowId;

  // Drop the card's fields from the shared form state (all of it when the canvas
  // has no identifiable card).
  const clearCardFields = useCallback(
    () =>
      setFormState((prev) =>
        cardFieldPaths
          ? {
              values: omitKeys(prev.values, cardFieldPaths),
              errors: omitKeys(prev.errors, cardFieldPaths),
              touched: new Set(
                Array.from(prev.touched).filter((k) => !cardFieldPaths.has(k)),
              ),
            }
          : { values: {}, errors: {}, touched: new Set() },
      ),
    [cardFieldPaths],
  );
  // Fill the card's fields from a record and leave every other field alone.
  const fillCardFields = useCallback(
    (record: Record<string, unknown>) =>
      setFormState((prev) =>
        cardFieldPaths
          ? {
              values: {
                ...omitKeys(prev.values, cardFieldPaths),
                ...cardValuesFromRecord(record, cardFieldPaths),
              },
              errors: omitKeys(prev.errors, cardFieldPaths),
              touched: new Set(
                Array.from(prev.touched).filter((k) => !cardFieldPaths.has(k)),
              ),
            }
          : { values: { ...record }, errors: {}, touched: new Set() },
      ),
    [cardFieldPaths],
  );

  // Initialize form values from canvas data (for edit mode)
  useEffect(() => {
    if (activeBinding && !selectedRowId) {
      if (initializedSelectionRef.current !== null) {
        initializedSelectionRef.current = null;
        clearCardFields();
      }
    } else if (
      selectedRecord &&
      (!activeBinding || initializedSelectionRef.current !== selectedRowId)
    ) {
      if (activeBinding) initializedSelectionRef.current = selectedRowId;
      if (activeBinding) {
        fillCardFields(selectedRecord);
      } else if (selectionBinding) {
        // A tab beside the card fills once per fetched primary record, not on every
        // render, so edits made there are not overwritten.
        const key = `${canvasData?.record_id ?? ""}|${loadedFor?.rowId ?? ""}`;
        if (
          initializedPrimaryRef.current !== key ||
          (primaryData && initializedPrimaryDataRef.current !== primaryData)
        ) {
          initializedPrimaryRef.current = key;
          initializedPrimaryDataRef.current = primaryData;
          setFormState({
            values: { ...selectedRecord },
            errors: {},
            touched: new Set(),
          });
        }
      } else {
        setFormState({
          values: { ...selectedRecord },
          errors: {},
          touched: new Set(),
        });
      }
    }
  }, [
    selectedRecord,
    selectedRowId,
    activeBinding,
    selectionBinding,
    canvasData?.record_id,
    loadedFor?.rowId,
    primaryData,
    clearCardFields,
    fillCardFields,
  ]);

  // Leaving the card's tab: the selected row's values must not stay in the form
  // the other tabs share. They are filled again when the card's tab is shown.
  const leftCardTab = !!selectionBinding && !activeBinding;
  if (activeBinding) initializedPrimaryRef.current = null;
  useEffect(() => {
    if (leftCardTab) {
      initializedSelectionRef.current = null;
      clearCardFields();
    }
  }, [leftCardTab, clearCardFields]);

  const handleSelectionChange = useCallback(
    (event: CanvasInteractionEvent) => {
      if (
        !selectionBinding ||
        event.elementId !== selectionBinding.source_grid_element_id
      )
        return;
      initializedSelectionRef.current = null;
      clearCardFields();
      setSelectedRowId(event.type === "row_selected" ? event.rowId : null);
      setSubmitted(false);
    },
    [selectionBinding, clearCardFields],
  );

  // Build grid config from canvas settings, falling back to defaults
  const gridConfig = useMemo(() => {
    if (!canvas) return DEFAULT_RUNTIME_GRID_CONFIG;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c = canvas as any;
    return {
      columns: c.grid_columns || DEFAULT_RUNTIME_GRID_CONFIG.columns,
      rowHeight: c.grid_row_height || DEFAULT_RUNTIME_GRID_CONFIG.rowHeight,
      gap: c.grid_gap ?? DEFAULT_RUNTIME_GRID_CONFIG.gap,
      minRows: DEFAULT_RUNTIME_GRID_CONFIG.minRows,
    };
  }, [canvas]);

  // Transform backend elements to RenderableElement format for the shared renderer
  const renderableElements = useMemo((): RenderableElement[] => {
    // The card's fields keep their own state keys (see canvasCardFieldKeys), so a personal
    // field elsewhere that binds the same field path is not cleared or filled with them.
    return namespaceCardFields(
      toRenderableElements(canvas || {}, {
        hasRow: !!rowId,
        canEdit: canvasData?.can_edit === true,
        canCreate: canvasData?.can_create === true,
        selectionMode: selectionBinding
          ? (selectionBinding.mode ?? "read")
          : undefined,
        selectionContainerId,
      }),
      (canvas?.elements || []) as ScopeNode[],
      selectionContainerId,
    );
  }, [
    canvas,
    canvasData?.can_create,
    canvasData?.can_edit,
    rowId,
    selectionBinding,
    selectionContainerId,
  ]);

  const canvasDataSources = useMemo((): CanvasDataSource[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawDataSources = ((canvas as any)?.data_sources || []) as any[];
    return rawDataSources.map((source, idx) => ({
      id: String(source.alias || source.id || `ds-${idx}`),
      name: String(source.alias || source.name || source.id || `ds-${idx}`),
      type: (source.type || "view") as CanvasDataSource["type"],
      view_id: source.view_id,
      schema_id: source.schema_id,
      is_primary: source.is_primary,
      config: source.config_json || source.config || {},
    }));
  }, [canvas]);

  // Get field elements that need form state management
  const fieldElements = useMemo(() => {
    return renderableElements.filter(
      (el) => el.type === "field" && el.data_binding?.field_path,
    );
  }, [renderableElements]);
  const writableFieldElements = useMemo(
    () =>
      fieldElements.filter(
        (element) =>
          isWritableAccess(element.effectiveAccess) &&
          !isReadOnlyFieldConfig(element.props),
      ),
    [fieldElements],
  );
  // Fields the footer submits: while the selection card is not on the shown tab,
  // the card's own fields are not part of the form being submitted.
  const submitFieldElements = useMemo(
    () =>
      selectionBinding && !activeBinding && offCardTabReadOnly
        ? // A read-only tab beside the card shows the primary record and submits
          // nothing; a ?row= on it must never turn into an update.
          []
        : selectionBinding && !activeBinding && selectionContainerId
          ? writableFieldElements.filter(
              (element) =>
                !isWithinContainer(
                  element.id,
                  selectionContainerId,
                  (canvas?.elements || []) as ScopeNode[],
                ),
            )
          : writableFieldElements,
    [
      activeBinding,
      canvas,
      offCardTabReadOnly,
      selectionBinding,
      selectionContainerId,
      writableFieldElements,
    ],
  );
  const boundElements = useMemo(
    () =>
      renderableElements.filter(
        (element) =>
          element.data_binding?.field_path &&
          (element.type === "field" || element.type === "static"),
      ),
    [renderableElements],
  );

  // Set a form value
  const setValue = useCallback((fieldPath: string, value: unknown) => {
    setInlineSubmitSuccess(false);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, [fieldPath]: value },
      errors: { ...prev.errors, [fieldPath]: "" },
    }));
  }, []);

  // Mark a field as touched
  const setTouched = useCallback((fieldPath: string) => {
    setFormState((prev) => ({
      ...prev,
      touched: new Set(prev.touched).add(fieldPath),
    }));
  }, []);

  // Validate a single field
  const validateField = useCallback(
    (element: RenderableElement): string | null => {
      const fieldPath = element.data_binding?.field_path;
      if (!fieldPath) return null;

      const value = formState.values[fieldPath];
      const props = element.props;

      // Required validation
      if (props.required) {
        if (value === null || value === undefined || value === "") {
          return `${props.label || "This field"} is required`;
        }
      }

      return null;
    },
    [formState.values],
  );

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    submitFieldElements.forEach((element) => {
      const error = validateField(element);
      const fieldPath = element.data_binding?.field_path;
      if (error && fieldPath) {
        newErrors[fieldPath] = error;
        isValid = false;
      }
    });

    setFormState((prev) => ({
      ...prev,
      errors: newErrors,
      touched: new Set(
        submitFieldElements.map((el) => el.data_binding?.field_path || ""),
      ),
    }));

    return isValid;
  }, [submitFieldElements, validateField]);

  // Build field state map for the renderer
  const fieldStateMap = useMemo((): Record<string, FieldState> => {
    const map: Record<string, FieldState> = {};

    boundElements.forEach((element) => {
      const fieldPath = element.data_binding?.field_path;
      if (fieldPath) {
        map[fieldPath] = {
          value: formState.values[fieldPath],
          error: formState.errors[fieldPath],
          touched: formState.touched.has(fieldPath),
          onChange: writableFieldElements.includes(element)
            ? (value: unknown) => setValue(fieldPath, value)
            : undefined,
          onBlur: writableFieldElements.includes(element)
            ? () => setTouched(fieldPath)
            : undefined,
        };
      }
    });

    return map;
  }, [boundElements, formState, setValue, setTouched, writableFieldElements]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!canvasId || !canvas) return;
    if (
      submitFieldElements.length === 0 ||
      (rowId ? canvasData?.can_edit !== true : canvasData?.can_create !== true)
    )
      return;
    if (
      activeBinding &&
      (activeBinding.mode !== "edit" ||
        !selectedRowId ||
        !selectedRowReady ||
        !canvasData?.can_edit)
    )
      return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Use the selected grid source for detail edits; other forms retain the
    // canvas primary-source default.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawDataSources = (canvas.data_sources || []) as any[];
    const primaryDataSource =
      rawDataSources.find((ds) => ds.is_primary) || rawDataSources[0];

    if (!primaryDataSource) {
      console.error("No data source found for canvas");
      return;
    }

    const primaryAlias = primaryDataSource.alias || primaryDataSource.id;
    const fieldAliases = new Set(
      submitFieldElements.map((element) => {
        const boundSource = element.data_binding?.data_source_id;
        if (!boundSource) return String(primaryAlias);
        const source = rawDataSources.find(
          (candidate) =>
            String(candidate.alias || candidate.id || "") ===
              String(boundSource) ||
            String(candidate.name || "") === String(boundSource),
        );
        return String(
          source?.alias || source?.name || source?.id || boundSource,
        );
      }),
    );
    if (!activeBinding && fieldAliases.size > 1) {
      setFormSourceError(MIXED_FORM_SOURCE_MESSAGE);
      return;
    }
    setFormSourceError(null);
    const dataSourceAlias =
      activeBinding?.data_source_alias ||
      (fieldAliases.size === 1 ? Array.from(fieldAliases)[0] : primaryAlias);

    const changedSelectedFields = activeBinding
      ? Object.fromEntries(
          submitFieldElements
            .filter(
              (element) =>
                element.data_binding?.data_source_id ===
                  activeBinding.data_source_alias &&
                !element.props.readonly &&
                !element.props.is_readonly,
            )
            .map((element) => element.data_binding?.field_path)
            .filter((fieldPath): fieldPath is string => !!fieldPath)
            .filter(
              (fieldPath) =>
                !Object.is(
                  formState.values[fieldPath],
                  selectedRecord?.[plainFieldPath(fieldPath)],
                ),
            )
            .map((fieldPath) => [
              plainFieldPath(fieldPath),
              formState.values[fieldPath],
            ]),
        )
      : null;
    if (activeBinding && Object.keys(changedSelectedFields || {}).length === 0)
      return;

    const submitPayload = {
      data_source_alias: dataSourceAlias,
      data:
        changedSelectedFields ||
        Object.fromEntries(
          submitFieldElements
            .map((element) => element.data_binding?.field_path)
            .filter((fieldPath): fieldPath is string => !!fieldPath)
            .map((fieldPath) => [
              plainFieldPath(fieldPath),
              formState.values[fieldPath],
            ]),
        ),
    };

    try {
      const submitResult = await submit(canvasId, submitPayload, rowId);
      if (submitResult.success) {
        const metadata = formView?.focus_table_id
          ? formView
          : (await refetchFormView()).data;
        if (metadata?.focus_table_id) {
          emitWorkspaceObjectChange({
            workspaceId,
            changedObjects: [
              {
                type: "table",
                id: String(metadata.focus_table_id),
                action: "updated",
              },
            ],
          });
        }
      }
      if (activeBinding && submitResult.success) {
        initializedSelectionRef.current = null;
      } else if (!activeBinding && submitResult.success) {
        const hasNonFormDataElements = renderableElements.some((element) =>
          [
            "table",
            "chart",
            "widget",
            "view_grid",
            "timeline",
            "kanban",
            "calendar",
            "hierarchy",
            "related_records",
            "embedded_canvas",
          ].includes(element.type),
        );
        if (hasNonFormDataElements) {
          setFormState({ values: {}, errors: {}, touched: new Set() });
          setInlineSubmitSuccess(true);
        } else {
          setSubmitted(true);
        }
      }
    } catch (err) {
      // Error is handled by the hook
    }
  };

  // Handle back navigation
  const handleBack = () => {
    const returnTo = (location.state as { canvasReturnTo?: string } | null)
      ?.canvasReturnTo;
    navigate(
      returnTo?.startsWith(`/workspace/${workspaceId}/myvault/`)
        ? returnTo
        : `/workspace/${workspaceId}/myvault/canvases`,
    );
  };

  // Handle edit mode navigation
  const handleEdit = () => {
    navigate(`/workspace/${workspaceId}/myvault/canvases/${canvasId}/edit`);
  };

  // Render loading state
  // A canvas with a selection binding never blocks on the data hook: it refetches on
  // every tab switch, and replacing the canvas with a spinner would unmount the tab
  // controls and send the user back to the first tab.
  const loading = canvasLoading || (!selectionBinding && dataLoading);
  const error = canvasError || (!selectionBinding && dataError);
  const runtimeNeedsPrimaryData = useMemo(
    () =>
      renderableElements.some(
        (element) =>
          element.type === "view_grid" || element.type === "embedded_canvas",
      ) ||
      ["browse", "report", "dashboard"].includes(
        String(
          (canvas as unknown as { archetype?: string } | null)?.archetype || "",
        ),
      ),
    [canvas, renderableElements],
  );
  const hasPrimaryDataEmptyState =
    runtimeNeedsPrimaryData &&
    !selectionBinding &&
    !rowId &&
    !submitted &&
    !!canvasData &&
    canvasData.record == null &&
    (canvasData.records?.length ?? 0) === 0 &&
    (canvasData.record_count ?? 0) === 0;

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (canvasErrorStatus === 404) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Canvas not found or not available.</Alert>
        <Button variant="plain" onClick={handleBack} sx={{ mt: 2 }}>
          Back to Apps
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{error}</Alert>
        <Button variant="plain" onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!canvas) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Canvas not found.</Alert>
        <Button variant="plain" onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const canvasAssistant =
    canvas.canvas_assistant_available === true ? (
      <FloatingChatWidget
        key={`${workspaceId}:${canvasId}`}
        workspaceId={workspaceId}
        canvasId={canvasId || undefined}
        allowCanvasRuntime
      />
    ) : null;

  if (renderableElements.length === 0) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.level1" }}>
        <Box
          sx={{
            p: 2,
            bgcolor: "background.surface",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            variant="plain"
            color="neutral"
            startDecorator={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Typography level="h4" sx={{ flex: 1 }}>
            {canvas.name}
          </Typography>
        </Box>
        <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
          <Alert color="neutral" variant="soft">
            <Stack spacing={1}>
              <Typography level="title-md">
                This canvas has no runtime content yet.
              </Typography>
              <Typography level="body-sm">
                Add runtime elements before sharing this canvas with users.
              </Typography>
            </Stack>
          </Alert>
        </Box>
        {canvasAssistant}
      </Box>
    );
  }

  // Success state after submission
  if (!activeBinding && submitted && result?.success) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <Typography level="h4" sx={{ color: "success.600", mb: 2 }}>
              Success!
            </Typography>
            <Typography level="body-md" sx={{ mb: 3 }}>
              {canvas.settings?.success_message ||
                "Your submission has been saved successfully."}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="outlined" onClick={handleBack}>
                Back to Canvases
              </Button>
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setFormState({ values: {}, errors: {}, touched: new Set() });
                }}
              >
                Submit Another
              </Button>
            </Stack>
          </CardContent>
        </Card>
        {canvasAssistant}
      </Box>
    );
  }

  // Render footer with submit button
  const renderFooter = () => {
    // The footer belongs to the form on the shown tab: a tab with only widgets or
    // read-only content has nothing to submit, even if another tab has fields.
    const shownSubmitFields = submitFieldElements.filter((element) =>
      isInActiveScope(
        element.id,
        (canvas?.elements || []) as ScopeNode[],
        activePanels,
      ),
    );
    if (
      shownSubmitFields.length === 0 ||
      (rowId ? canvasData?.can_edit !== true : canvasData?.can_create !== true)
    )
      return null;
    if (
      activeBinding &&
      (activeBinding.mode !== "edit" || !selectedRowId || !canvasData?.can_edit)
    )
      return null;

    return (
      <>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="plain"
            color="neutral"
            onClick={
              activeBinding
                ? () => fillCardFields(selectedRecord || {})
                : handleBack
            }
          >
            Cancel
          </Button>
          <Button
            startDecorator={<SendIcon />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!!activeBinding && !selectedRowReady}
          >
            {activeBinding
              ? "Save"
              : canvas.settings?.submit_button_text ||
                (isEditMode ? "Update" : "Submit")}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.level1" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: "background.surface",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          variant="plain"
          color="neutral"
          startDecorator={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Typography level="h4" sx={{ flex: 1 }}>
          {canvas.name}
        </Typography>
        {isEditor && (
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<EditIcon />}
            onClick={handleEdit}
          >
            Edit Canvas
          </Button>
        )}
      </Box>

      {/* Form Content */}
      <Box
        data-testid="canvas-runtime-content"
        sx={{
          p: 3,
          maxWidth: canvasContentMaxWidth(
            (canvas as unknown as { theme_config?: unknown })?.theme_config,
          ),
          mx: "auto",
        }}
      >
        <Card>
          <CardContent>
            {/* Render errors */}
            {submitError && (
              <Alert color="danger" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}

            {inlineSubmitSuccess && (
              <Alert color="success" sx={{ mb: 3 }}>
                {canvas.settings?.success_message ||
                  "Your submission has been saved successfully."}
              </Alert>
            )}

            {(formSourceError || hasMixedFormSources) && (
              <Alert color="danger" sx={{ mb: 3 }}>
                {formSourceError || MIXED_FORM_SOURCE_MESSAGE}
              </Alert>
            )}

            {result?.errors && Object.keys(result.errors).length > 0 && (
              <Alert color="danger" sx={{ mb: 3 }}>
                <Typography level="body-sm" fontWeight="lg">
                  Please fix the following errors:
                </Typography>
                <ul>
                  {Object.entries(result.errors).map(([field, messages]) => (
                    <li key={field}>
                      {field}: {messages.join(", ")}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {hasPrimaryDataEmptyState && (
              <Alert color="neutral" sx={{ mb: 3 }}>
                <Stack spacing={0.5}>
                  <Typography level="title-sm">No records yet.</Typography>
                  <Typography level="body-sm">
                    This canvas is ready, but its primary data source has not
                    produced any records.
                  </Typography>
                </Stack>
              </Alert>
            )}

            {/* Use CanvasRenderer for grid-based layout.
                CanvasRuntimeProvider threads the workspace / canvas / row
                identifiers down to leaf elements (notably Action Button)
                without prop-drilling through every nested renderer. */}
            <CanvasRuntimeProvider
              value={{
                onRefresh: async () => {
                  initializedSelectionRef.current = null;
                  await refreshCanvasData();
                },
                workspaceId,
                canvasId,
                dataSources: canvasDataSources,
                canEdit: canvasData?.can_edit === true,
                rowId: rowId || canvasData?.record_id || null,
                primaryRowId: urlRowId || settledPrimaryIdentity.current.rowId,
                // Actions template {{field}} server-side from this row data, so on
                // the card's tab it is the whole selected record (the card's edits
                // override it); elsewhere it is just the form.
                rowData:
                  activeBinding && selectedRecord
                    ? {
                        ...selectedRecord,
                        ...nonCardValues(formState.values),
                        ...cardValuesByPlainPath(formState.values),
                      }
                    : nonCardValues(formState.values),
                selectionDetail: selectionBinding
                  ? {
                      rowId: selectedRowId,
                      loading:
                        !!selectedRowId &&
                        (dataLoading ||
                          !selectedRecord ||
                          initializedSelectionRef.current !== selectedRowId),
                      error: dataError,
                    }
                  : undefined,
                onSelectionChange: handleSelectionChange,
              }}
            >
              <CanvasThemeScope
                themeConfig={
                  (canvas as unknown as { theme_config?: unknown })
                    ?.theme_config
                }
                color={(canvas as unknown as { color?: string })?.color}
              >
                <CanvasWidgetDataContext.Provider value={canvasDataSources}>
                  <CanvasRenderer
                    elements={renderableElements}
                    config={gridConfig}
                    mode="runtime"
                    responsive
                    fieldState={fieldStateMap}
                    onActivePanelChange={handleActivePanelChange}
                    renderEmbeddedCanvas={(element) => {
                      const parentKeyField = (
                        element.props as unknown as { parentKeyField?: string }
                      ).parentKeyField;
                      const parentRecordId =
                        rowId || canvasData?.record_id || null;
                      const parentData: Record<string, unknown> = {
                        ...nonCardValues(formState.values),
                      };

                      if (parentRecordId) {
                        if (parentData.id === undefined)
                          parentData.id = parentRecordId;
                        if (
                          parentKeyField &&
                          parentData[parentKeyField] === undefined
                        ) {
                          parentData[parentKeyField] = parentRecordId;
                        }
                      }

                      const embeddedElement: CanvasElement = {
                        id: element.id,
                        type: element.type as CanvasElement["type"],
                        props: element.props,
                        data_binding: element.data_binding as any,
                        layout: {
                          gridColumn: element.gridColumn,
                          gridRow: element.gridRow,
                          columnSpan: element.columnSpan,
                          rowSpan: element.rowSpan,
                        },
                        parentElementId: element.parentElementId,
                        parentPanelId: element.parentPanelId,
                        displayOrder: element.displayOrder,
                      };

                      return (
                        <EmbeddedCanvasRenderer
                          element={embeddedElement}
                          parentData={parentData}
                          workspaceId={workspaceId}
                          parentCanvasId={canvasId || undefined}
                          allowCreate={isWritableAccess(
                            element.effectiveAccess,
                          )}
                          allowEdit={isWritableAccess(element.effectiveAccess)}
                          allowDelete={isWritableAccess(
                            element.effectiveAccess,
                          )}
                        />
                      );
                    }}
                    renderViewGrid={(element) => (
                      <CanvasViewGridRenderer
                        element={element}
                        dataSources={canvasDataSources}
                        canvasId={canvasId}
                      />
                    )}
                    renderTimeline={(element) => (
                      <CanvasTimelineRenderer
                        element={element}
                        dataSources={canvasDataSources}
                        canvasId={canvasId}
                      />
                    )}
                    renderDataWidget={(element) => (
                      <CanvasDataWidgetRenderer
                        element={element}
                        dataSources={canvasDataSources}
                        canvasId={canvasId}
                      />
                    )}
                    title={canvas.name}
                    description={canvas.description}
                    footer={renderFooter()}
                  />
                </CanvasWidgetDataContext.Provider>
              </CanvasThemeScope>
            </CanvasRuntimeProvider>
          </CardContent>
        </Card>
      </Box>
      {canvasAssistant}
    </Box>
  );
}
